import React, { useEffect, useState } from 'react';

import { Hit, SearchResponse } from '@algolia/client-search';

import NewsSectionArticle from '@/components/pages/frontpage/components/News/NewsSectionArticle';
import { AlgoliaPostHit } from '@/types/algolia/AlgoliaPostHit';
import { ArticleType } from '@/types/ArticleType';
import mapAlgoliaHits from '@/utils/functions/algolia/mapAlgoliaHits';
import queryAlgoliaPosts from '@/utils/functions/algolia/queryAlgoliaPosts';

type SearchPageLayoutProps = {
    initialResults: SearchResponse<any>;
};

// eslint-disable-next-line import/prefer-default-export
const SearchPageLayout = (props: SearchPageLayoutProps) => {
    const { initialResults } = props;
    const [limit] = useState(12);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState(initialResults);
    const [hits, setHits] = useState<ArticleType[]>(mapAlgoliaHits(initialResults.hits));
    const [page, setPage] = useState(0);

    useEffect(() => {
        queryAlgoliaPosts(searchTerm, limit, page)?.then(queryResults => {
            setResults(queryResults);
            page > 0
                ? setHits([...hits, ...mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>)])
                : setHits(mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>));
        });
    }, [searchTerm, page]);

    return (
        <div className="w-full flex-col">
            <div className="flex p-6 md:p-10 lg:p-12 bg-gray-50">
                <div className="mx-auto w-full lg:w-[80%]">
                    <input
                        id="blockworks-search"
                        type="text"
                        onChange={ev => {
                            setPage(0);
                            setSearchTerm(ev.target.value);
                        }}
                        placeholder="Search..."
                        className="w-full min-w-0 appearance-none rounded-none border border-gray-200 hover:border-gray-300 bg-white py-2 px-4 text-base text-gray-900 placeholder-gray-300 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        autoFocus
                    />
                </div>
            </div>
            <div className="p-8">
                <div className="w-full flex place-content-center pb-4 -mt-4">
                    <p className="text-xxs font-light flex flex-row gap-1">
                        <span className="text-gray-400">
                            Page {page + 1}/{Math.floor(results.nbHits / limit)} -{' '}
                        </span>
                        <span className="text-gray-400">{results.nbHits} total hits</span>
                        <span className="text-gray-300">(~{results.processingTimeMS}ms)</span>
                    </p>
                </div>

                <div className="grid gap-8 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                    {hits.length === 0 && (
                        <div>
                            <p className="text-lg">Your search did not return any results.</p>
                        </div>
                    )}
                    {hits.map(article => (
                        <NewsSectionArticle article={article} key={`NewsSectionArticle-${article.slug}`} />
                    ))}
                </div>
            </div>
            {hits.length > 0 && results && results.nbHits > hits.length * page && (
                <div className="p-12 w-full">
                    <div className="mx-auto">
                        <button
                            type="button"
                            onClick={() => setPage(page + 1)}
                            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            Load More
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchPageLayout;
